import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogIn, Shield } from 'lucide-react';
import { authAPI } from '../services/api';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function MemberLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    let value = e.target.value.toUpperCase();

    // Remove all non-alphanumeric characters except hyphens
    value = value.replace(/[^A-Z0-9-]/g, '');

    // Remove existing hyphens to rebuild format
    value = value.replace(/-/g, '');

    // Ensure it starts with SAS
    if (!value.startsWith('SAS')) {
      if (value.length === 0) {
        value = 'SAS-';
      } else if (value.startsWith('SA')) {
        value = 'SAS' + value.substring(2);
      } else if (value.startsWith('S')) {
        value = 'SAS' + value.substring(1);
      } else {
        value = 'SAS' + value;
      }
    }

    // Remove 'SAS' prefix for formatting
    const afterSAS = value.substring(3);

    // Format as SAS-XXXX-XXXX
    let formatted = 'SAS';
    if (afterSAS.length > 0) {
      formatted += '-' + afterSAS.substring(0, 4);
    }
    if (afterSAS.length > 4) {
      formatted += '-' + afterSAS.substring(4, 8);
    }

    setCode(formatted);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authAPI.memberLogin(code);
      // Save member code to localStorage for browser extension
      localStorage.setItem('sas_member_code', code);
      navigate('/portal/dashboard');
    } catch (err: any) {
      setError(err.message || t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSwitcher />
      </div>
      <div className="bg-white dark:bg-warm-gray-800 dark:border dark:border-warm-gray-700 rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">Smartphone Analysis System</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('login.memberLogin')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-2">
              {t('login.registrationCode')}
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="SAS-XXXX-XXXX"
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-warm-gray-600 dark:bg-warm-gray-700 dark:text-white rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none font-mono"
              required
              disabled={loading}
              maxLength={13}
            />
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors shadow-lg"
          >
            <LogIn className="w-5 h-5" />
            <span>{loading ? t('login.loggingIn') : t('login.loginButton')}</span>
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-warm-gray-700 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('login.adminLink')}</p>
          <button
            onClick={() => navigate('/admin')}
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            {t('login.adminLogin')}
          </button>
        </div>
      </div>
    </div>
  );
}
